import { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../../config/constants';

interface NdaFile {
    name: string;
    fullPath: string;
    url: string;
    size: number;
    createdOn: string | null;
    lastModified: string | null;
}

export default function useNdaFiles() {
    const [files, setFiles] = useState<NdaFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadNdaFiles = useCallback(async (userId?: string | number) => {
        setLoading(true);
        setError(null);

        try {
            const body = userId ? JSON.stringify({ user_id: userId }) : undefined;
            const response = await fetch(`${API_BASE}/api/users/get-nda-files`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
            });

            if (!response.ok) throw new Error(`Failed to fetch NDA files: ${response.statusText}`);

            const result = await response.json();
            if (result.success && Array.isArray(result.data)) setFiles(result.data);
            else setFiles([]);
        } catch (err: any) {
            setError(err?.message || 'Failed to load NDA files');
            setFiles([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadNdaFiles();
    }, [loadNdaFiles]);

    const getNdaUserPermissions = useCallback(async (userId: string | number) => {
        if (!userId) throw new Error('userId is required');

        const res = await fetch(`${API_BASE}/api/users/get-user-nda-files`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId }),
        });

        if (!res.ok) throw new Error(`Failed to fetch NDA permissions: ${res.statusText}`);
        return res.json();
    }, []);

    return { files, loading, error, reload: loadNdaFiles, getNdaUserPermissions } as const;
}
